import React, { useState, useEffect } from 'react';
import { motion } from "motion/react";
import type { MediaResponse } from '@/features/clubs/types/types';
import { getSignedMediaUrl } from '@/lib/utils';
import ImagePlaceholder from './ImagePlaceholder';
import ScrollingBanner from './ScrollingBanner';

interface ClubHeroProps {
  clubName: string;
  media: MediaResponse[] | null;
  heroSection?: {
    title: string;
    body: string;
  };
}

const ClubHero: React.FC<ClubHeroProps> = ({ clubName, media, heroSection }) => {
  const [scrollY, setScrollY] = useState(0);

  // (sagnik) Removed carousel functionality - only using rank 0 video as static background -- suggest any better way to do this

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  

  const titleOpacity = Math.max(0, 1 - scrollY / 300);
  const titleScale = Math.max(0.7, 1 - scrollY / 1000); // Scale down from 1 to 0.7
  const bannerOpacity = Math.max(0, 1 - scrollY / 200);
  const backgroundBlur = Math.min(20, scrollY / 15); // Blur increases as user scrolls
  const backgroundVideoScale = Math.min(1.1, 1 + scrollY / 2000); // Scale up from 1 to 1.1


  // Get rank 0 video for main background
  const mainVideo = media?.find(item => item.rank === 0);

  return (
    <>
      
      <section className="relative h-[100vh] bg-black">
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          {/* Main video background (rank 0) */}
          {mainVideo ? (
            <div className="absolute inset-0 z-0">
              <video
                autoPlay
                muted
                loop
                playsInline
                className="h-full w-full object-cover"
                style={{ 
                  filter: `blur(${backgroundBlur}px)`,
                  transform: `scale(${backgroundVideoScale})`,
                  willChange: 'filter, transform',
                  transition: 'filter 0.3s ease-out, transform 0.3s ease-out'
                }}
                onError={(e) => {
                  const target = e.target as HTMLVideoElement;
                  target.style.display = 'none';
                  // Show fallback image instead
                  const fallbackImg = target.nextElementSibling as HTMLImageElement;
                  if (fallbackImg) {
                    fallbackImg.style.display = 'block';
                  }
                }}
              >
                <source src={getSignedMediaUrl(mainVideo.media_id, 'video').url} type="video/mp4" />
                <source src={getSignedMediaUrl(mainVideo.media_id, 'video').url} type="video/webm" />
                Your browser does not support the video tag.
              </video>
              {/* Fallback image if video fails */}
              <img
                src={getSignedMediaUrl(mainVideo.media_id, 'image').url}
                alt={`${clubName} background`}
                className="h-full w-full object-cover hidden"
                style={{ 
                  filter: `blur(${backgroundBlur}px)`,
                  transform: `scale(${backgroundVideoScale})`,
                  willChange: 'filter, transform',
                  transition: 'filter 0.3s ease-out, transform 0.3s ease-out'
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <ImagePlaceholder 
                label="Video/Image Loading..." 
                className="h-full w-full !rounded-none hidden"
                style={{ 
                  filter: `blur(${backgroundBlur}px)`,
                  transform: `scale(${backgroundVideoScale})`,
                  willChange: 'filter, transform',
                  transition: 'filter 0.3s ease-out, transform 0.3s ease-out'
                }}
              />
              
              {/* Gradient overlay from 40% black at bottom to transparent at top */}
              <div 
                className="absolute inset-0 z-10"
                style={{
                  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.2) 30%, transparent 100%)'
                }}
              ></div>
            </div>
          ) : (
            <div className="absolute inset-0 bg-black z-0">
              <ImagePlaceholder 
                label="Main Background" 
                className="h-full w-full !rounded-none"
                style={{ 
                  filter: `blur(${backgroundBlur}px)`,
                  transform: `scale(${backgroundVideoScale})`,
                  willChange: 'filter, transform',
                  transition: 'filter 0.3s ease-out, transform 0.3s ease-out'
                }}
              />
              <div className="absolute inset-0 bg-black/60 z-10"></div>
            </div>
          )}
          
          <div className="absolute z-20 flex h-full w-full items-center justify-center pointer-events-none" style={{ opacity: titleOpacity, transform: `scale(${titleScale})`, willChange: 'opacity, transform' }}>
            <div className="text-center max-w-6xl px-8">
              <motion.h1 
                className="text-6xl text-center md:text-9xl font-serif font-medium tracking-tighter text-white drop-shadow-2xl mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
              >
                {heroSection?.title || clubName}
              </motion.h1>
              {heroSection?.body && (
                <motion.p 
                  className="text-xl md:text-2xl text-gray-200 drop-shadow-lg max-w-4xl mx-auto leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1, ease: "easeOut" }}
                >
                  {heroSection.body}
                </motion.p>
              )}
            </div>
          </div>
          
          
          {/* Scrolling Banner with fade-out animation */}
          <motion.div
            className={`absolute bottom-0 left-0 w-full z-40 ${bannerOpacity < 0.5 ? 'pointer-events-none' : ''}`}
            style={{ opacity: bannerOpacity, willChange: 'opacity' }}
          >
            <ScrollingBanner />
          </motion.div>


        </div>
      </section>
    </>
  );
};

export default ClubHero;

